import {
  AuthSelectors,
  AuthTypes,
  ConfigActions,
  ConfigSelectors,
  LanguageSelectors,
  StatsActions,
  StatsSelectors,
  waitForFirebaseReadySaga,
} from '@web_opxp/features';
import { all, call, delay, fork, put, select, take, takeEvery } from 'redux-saga/effects';
import { ApiService } from 'web_core_library';
import { AssessmentRoute, getAssessmentConfigForCycle } from '../assessment';
import { WORKOUT_PROGRESS_MIGRATION } from '../base/constants';
import { redirectInternal, redirectToOnboarding } from '../base/historyService';
import { IAssessmentConfigOPXP } from '../base/types';
import { CyclesActions, CyclesSelectors } from '../cycles';
import { DashboardRoute } from '../dashboard/Dashboard';
import { RETRY_LIMIT, STATS_FETCHING_DELAY } from './constants';
import * as FirebaseMigrationActions from './firebaseActions';
import Service from './service';
import { MigrationActions } from './slice';
import { EMigrationFlag, MigrationError } from './types';

// initialize migration feature
export function* initializeMigration() {
  yield call(waitForFirebaseReadySaga);
  const userId = yield select(AuthSelectors.getUserId);
  const token = yield select(AuthSelectors.getToken);
  if (!userId || !token) {
    throw new AuthTypes.AuthError('common.common:Errors.Unauthorized');
  }
  yield call(Service.init, ApiService);
  const statsLoading = yield select(StatsSelectors.isStatsLoading);
  if (statsLoading) {
    yield take(StatsActions.statsReady);
  }
  yield put(MigrationActions.featureStarted());
  yield fork(detectMigrationStep);
}

export function* detectMigrationStep() {
  const migrationFlag: EMigrationFlag | undefined = yield select(
    StatsSelectors.getStatIntValueById,
    WORKOUT_PROGRESS_MIGRATION
  );
  switch (migrationFlag) {
    case EMigrationFlag.MIGRATION_CONFIRMED_BY_APP:
      yield put(MigrationActions.completeMigration());
      return;
    case EMigrationFlag.MIGRATION_FINISHED_BY_BACKEND:
      yield put(MigrationActions.migrationBackendFinished());
      return;
    case EMigrationFlag.MIGRATION_STARTED:
      yield put(MigrationActions.migrationBackendStarted());
      return;
    default:
      yield put(MigrationActions.startMigration());
      return;
  }
}

export function* startMigrationSaga() {
  // send firebase action that user start migration
  yield put(FirebaseMigrationActions.migrationStartedAction());
  // here we should call users_migrate_workouts api call
  for (let i = 0; i < RETRY_LIMIT; i++) {
    try {
      // will throw error if failed
      yield call([Service, 'startMigration']);
      yield put(MigrationActions.migrationBackendStarted());
      return;
    } catch (error) {
      // wait for a while then try again
      yield delay(STATS_FETCHING_DELAY);
    }
  }
  // somethings went wrong if we got here
  throw new MigrationError('Migration failed');
}

export function* continueMigrationSaga() {
  // here we should wait until user stat will be changed to "finished by backend"
  yield delay(STATS_FETCHING_DELAY);
  // load stats
  yield put(StatsActions.loadStats());
  yield take(StatsActions.updateStats);
  // try to detect migration status
  yield fork(detectMigrationStep);
}

export function* reloadCycles() {
  const cyclesLoading = yield select(CyclesSelectors.isCyclesLoading);
  // if cycles are loading they will be loaded already in the actual state
  if (!cyclesLoading) {
    // if cycles were loaded before we need to start loading again as they might have changed
    yield put(CyclesActions.loadCycles());
  }
  // wait until cycles data is loaded
  yield take(CyclesActions.cyclesReady);
}

export function* finishMigrationSaga() {
  yield call(initCycles);
  yield call(reloadCycles);
  // if cycle completed create new assessment
  const cycleCompleted = yield select(CyclesSelectors.isCycleCompleted);
  if (cycleCompleted) {
    yield call(waitForFirebaseReadySaga);
    // create cycle with assessment
    const cycleId = yield select(CyclesSelectors.getCurrentCycleId);
    const nextCycleId = cycleId + 1;
    const assessmentConfig: IAssessmentConfigOPXP = yield call(getAssessmentConfigForCycle, nextCycleId);
    yield put(CyclesActions.createAssessment(assessmentConfig, false));
    yield take(CyclesActions.assessmentCreated);
  }
  // in the end we should set user stat to "confirmed by app"
  yield put(StatsActions.updateNumericStat(WORKOUT_PROGRESS_MIGRATION, EMigrationFlag.MIGRATION_CONFIRMED_BY_APP));
  yield put(StatsActions.saveStatsQueue());
  yield take(StatsActions.saveStatsQueueSuccess);
  yield put(MigrationActions.completeMigration());
}

export function* completeMigrationSaga() {
  // send firebase action that user finished migration
  yield put(FirebaseMigrationActions.migrationFinishedAction());
  // here we should redirect to dashboard or assessment or onboarding
  const onboardingFinished = yield select(CyclesSelectors.isOnboardingFinished);
  if (!onboardingFinished) {
    const token = yield select(AuthSelectors.getToken);
    const language = yield select(LanguageSelectors.getLanguage);
    return redirectToOnboarding(language, token);
  }
  const assessmentFinished = yield select(CyclesSelectors.isAssessmentFinished);
  if (assessmentFinished) {
    return redirectInternal(DashboardRoute);
  }
  return redirectInternal(AssessmentRoute);
}

export function* initCycles() {
  const cyclesLoading = yield select(CyclesSelectors.isCyclesLoading);
  if (!cyclesLoading) {
    // feature was started already
    return;
  }
  const userId: number = yield select(AuthSelectors.getUserId);
  const isPremium: boolean = yield select(AuthSelectors.isPremium);
  // load cycle statistics and assessment session of current cycle
  yield put(CyclesActions.initCycles(userId, isPremium));
  yield take(CyclesActions.cyclesReady);
}

export function* initConfig() {
  const configLoading = yield select(ConfigSelectors.isConfigLoading);
  if (!configLoading) {
    // feature was started already
    return;
  }
  const userId: number = yield select(AuthSelectors.getUserId);
  yield put(ConfigActions.initConfig(userId));
  yield take(ConfigActions.configReady);
}

// base watcher for migration actions
export default function* watchMigration() {
  yield all([
    takeEvery(MigrationActions.initMigration, initializeMigration),
    takeEvery(MigrationActions.startMigration, startMigrationSaga),
    takeEvery(MigrationActions.migrationBackendStarted, continueMigrationSaga),
    takeEvery(MigrationActions.migrationBackendFinished, finishMigrationSaga),
    takeEvery(MigrationActions.completeMigration, completeMigrationSaga),
  ]);
}
